/**
 * A chart spec → a PNG, entirely in the browser.
 *
 * The chart is RE-DRAWN off-screen rather than captured from the one on screen. Both are possible
 * (Highcharts' `exporting` module is already registered — see highcharts-with-modules), but a
 * capture inherits whatever the live chart happens to be: the reader's panel width, the app's dark
 * mode, a transparent background, screen resolution. A document needs none of those, so the export
 * renders its own chart at a fixed size on white, and the picture is identical whoever exports it.
 *
 * Rasterizing goes through an `<img>` + `<canvas>`: `getSVG()` gives standalone SVG, the browser
 * draws it, `toBlob` hands back PNG bytes. `exportChartLocal()` would do the same conversion but
 * only to trigger a download, and the offline module's own `svgToDataUrl` is internal.
 */
import { Highcharts } from "$root/third-party-integrations/highcharts-with-modules";
import { buildChartOptions } from "../charts/buildChartOptions";
import type { ChartSpec } from "../charts/chartSpec";
import type { DocImage } from "./docModel";

/** Drawing size in CSS px. 16:9-ish, and wide enough that axis labels don't collide. */
const RENDER_WIDTH = 1000;
const RENDER_HEIGHT = 560;

/** Rasterize at 2× so the picture still looks sharp printed, or zoomed in Word. */
const RASTER_SCALE = 2;

/**
 * Placement width in the document, in px at 96 DPI: 6.5in of text on a Letter/A4 page with 1in
 * margins. Height follows the render aspect ratio so nothing is squashed.
 */
const PLACEMENT_WIDTH = 624;
const PLACEMENT_HEIGHT = Math.round((PLACEMENT_WIDTH * RENDER_HEIGHT) / RENDER_WIDTH);

/**
 * A font the OS is certain to have. The SVG is rasterized as an isolated document, which does NOT
 * load the app's web fonts — asking for them would silently fall back to a serif face, so the
 * export asks for what it can actually get.
 */
const EXPORT_FONT = "Arial, Helvetica, sans-serif";

/** Renders `spec` as a picture ready to place in the document. Rejects if the browser can't rasterize. */
export async function renderChartImage(spec: ChartSpec): Promise<DocImage> {
	const container = document.createElement("div");
	// Off-screen but LAID OUT: Highcharts measures its container, and `display: none` would give it
	// nothing to measure. The explicit chart width/height below makes the size deterministic anyway.
	container.style.cssText = `position:fixed;left:-20000px;top:0;width:${RENDER_WIDTH}px;height:${RENDER_HEIGHT}px;`;
	container.setAttribute("aria-hidden", "true");
	document.body.appendChild(container);

	let chart: Highcharts.Chart | undefined;
	try {
		const base = buildChartOptions(spec);
		chart = Highcharts.chart(container, {
			...base,
			chart: {
				...base.chart,
				width: RENDER_WIDTH,
				height: RENDER_HEIGHT,
				// The live chart is transparent and inherits the app's font (see buildChartOptions) —
				// on a page, that means invisible-on-white and a fallback typeface.
				backgroundColor: "#ffffff",
				style: { fontFamily: EXPORT_FONT },
				animation: false,
			},
		});
		const png = await svgToPng(chart.getSVG(), RENDER_WIDTH, RENDER_HEIGHT);
		return {
			type: "image",
			png,
			width: PLACEMENT_WIDTH,
			height: PLACEMENT_HEIGHT,
			alt: spec.title ?? `${spec.chartType} chart`,
		};
	} finally {
		chart?.destroy();
		container.remove();
	}
}

async function svgToPng(svg: string, width: number, height: number): Promise<Uint8Array> {
	const svgUrl = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml;charset=utf-8" }));
	try {
		const image = await loadImage(svgUrl);
		const canvas = document.createElement("canvas");
		canvas.width = width * RASTER_SCALE;
		canvas.height = height * RASTER_SCALE;
		const context = canvas.getContext("2d");
		if (!context) throw new Error("Canvas 2D context unavailable");
		// The SVG's own background is opaque white, but a canvas starts transparent and PNG keeps
		// transparency — so anything the chart doesn't cover would show the page through it.
		context.fillStyle = "#ffffff";
		context.fillRect(0, 0, canvas.width, canvas.height);
		context.drawImage(image, 0, 0, canvas.width, canvas.height);
		const png = await canvasToBlob(canvas);
		return new Uint8Array(await png.arrayBuffer());
	} finally {
		URL.revokeObjectURL(svgUrl);
	}
}

function loadImage(url: string): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const image = new Image();
		image.onload = () => resolve(image);
		image.onerror = () => reject(new Error("Could not rasterize the chart SVG"));
		image.src = url;
	});
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
	return new Promise((resolve, reject) => {
		canvas.toBlob(
			(blob) => (blob ? resolve(blob) : reject(new Error("Could not encode the chart as PNG"))),
			"image/png",
		);
	});
}
